import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Toaster, toast } from 'sonner';
import { Loader2, AlertTriangle, CheckCircle2, Monitor } from 'lucide-react';
import { BrandMark } from '../components/assessment/BrandMark';
import RegisterStep from '../components/assessment/RegisterStep';
import SkillPicker from '../components/assessment/SkillPicker';
import TestRunner from '../components/assessment/TestRunner';
import { getPublicAssessment, startAssessment, resumeAssessment, apiError } from '../services/assessmentApi';

const Shell = ({ children }) => (
  <div className="min-h-screen bg-[#F7F8FB] text-slate-900" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(15,23,42,0.06) 1px, transparent 0)', backgroundSize: '22px 22px' }}>
    <header className="h-16 border-b border-slate-200 bg-white/90 backdrop-blur px-6 flex items-center justify-between">
      <BrandMark />
      <span className="text-xs text-slate-500 hidden sm:block">Secure assessment session</span>
    </header>
    <main className="max-w-6xl mx-auto px-6 py-12">{children}</main>
    <Toaster richColors position="top-center" />
  </div>
);

const Message = ({ icon: Icon, tone, title, text, testid }) => (
  <div className="max-w-lg mx-auto text-center bg-white border border-slate-200 rounded-2xl p-10 shadow-sm" data-testid={testid}>
    <div className={`w-14 h-14 rounded-2xl mx-auto flex items-center justify-center ${tone}`}><Icon className="h-7 w-7" /></div>
    <h1 className="mt-5 text-2xl font-extrabold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>{title}</h1>
    <p className="mt-2 text-slate-600 text-sm leading-relaxed">{text}</p>
    <p className="mt-8 text-[11px] text-slate-400">HireFast · TekWissen Evolution in AI</p>
  </div>
);

export default function AssessmentTakePage() {
  const { token } = useParams();
  const storeKey = `hf_assess_${token}`;
  const [info, setInfo] = useState(null);
  const [step, setStep] = useState('loading');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', email: '', consent: false });
  const [skills, setSkills] = useState([]);
  const [attempt, setAttempt] = useState(null);
  const [starting, setStarting] = useState(false);
  const [doneName, setDoneName] = useState('');
  const small = typeof window !== 'undefined' && window.innerWidth < 900;

  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getPublicAssessment(token);
        setInfo(data);
        if (data.invite) setForm((f) => ({ ...f, name: data.invite.name || '', email: data.invite.email || '' }));
        const saved = JSON.parse(localStorage.getItem(storeKey) || 'null');
        if (saved) {
          const r = await resumeAssessment(saved.attempt_id, saved.key).catch(() => null);
          if (r?.status === 'in_progress') { setAttempt(r); setStep('running'); return; }
          if (r?.status === 'completed') { setDoneName(r.candidate_name); setStep('done'); return; }
        }
        setStep(data.invite?.status === 'completed' ? 'done' : 'register');
      } catch (e) {
        setError(apiError(e, 'This assessment link is not available.'));
        setStep('error');
      }
    })();
  }, [token, storeKey]);

  const start = async () => {
    setStarting(true);
    try {
      const r = await startAssessment(token, { name: form.name.trim(), email: form.email.trim(), skills });
      localStorage.setItem(storeKey, JSON.stringify({ attempt_id: r.attempt_id, key: r.attempt_key }));
      setAttempt(r);
      setStep('running');
    } catch (e) {
      toast.error(apiError(e, 'Could not start the assessment'));
    } finally {
      setStarting(false);
    }
  };

  if (step === 'running' && attempt) {
    return (
      <>
        <TestRunner attempt={attempt} onDone={() => { setDoneName(attempt.candidate_name); setStep('done'); }} />
        <Toaster richColors position="top-center" />
      </>
    );
  }

  return (
    <Shell>
      {step === 'loading' && <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>}
      {step === 'error' && <Message icon={AlertTriangle} tone="bg-red-50 text-red-600" title="Link unavailable" text={error} testid="assessment-link-error" />}
      {step === 'done' && (
        <Message icon={CheckCircle2} tone="bg-emerald-50 text-emerald-600" testid="assessment-done-screen"
          title={`Thank you${doneName ? `, ${doneName.split(' ')[0]}` : ''}!`}
          text="Your assessment has been submitted and is being evaluated. The hiring team will review your results and get in touch with next steps." />
      )}
      {small && (step === 'register' || step === 'skills') && (
        <div className="mb-6 flex items-center gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3" data-testid="desktop-notice">
          <Monitor className="h-4 w-4 shrink-0" /> This test includes a live spreadsheet — please use a laptop or desktop.
        </div>
      )}
      {step === 'register' && info && (
        <RegisterStep test={info.test} invite={info.invite} form={form} setForm={setForm} onNext={() => setStep('skills')} />
      )}
      {step === 'skills' && info && (
        <SkillPicker skills={info.skills} min={info.test.min_skills} max={info.test.max_skills} selected={skills} setSelected={setSkills}
          onBack={() => setStep('register')} onStart={start} starting={starting} />
      )}
    </Shell>
  );
}
